import { useEffect, useRef } from "react";
import styled, { css } from "styled-components";
import { getConfirmationModalData } from "../../store/appSlice";
import { useMainStore } from "../../store/mainStore";
import { Body, H3 } from "../../styles/stringStyles";
import { ButtonGroup } from "../buttons/ButtonGroup";
import { useConfirmation } from "./useEventIndicator";

export const ConfirmationModal = () => {
  const confirmationModalData = useMainStore(getConfirmationModalData);
  const setConfirmationModalData = useConfirmation();
  const dismiss = useMainStore((state) => state.dismissConfirmationModal);

  const overlayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    return () => dismiss();
  }, [dismiss]);

  const handleCancel = () => {
    confirmationModalData?.resolve?.(false);
    setConfirmationModalData(undefined);
  };
  const handleConfirm = () => {
    confirmationModalData?.resolve?.(true);
    setConfirmationModalData(undefined);
  };

  const handleOverlayClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!overlayRef.current || event.target !== overlayRef.current) return;
    dismiss();
  };

  if (!confirmationModalData) {
    return null;
  }

  return (
    <ModalOverlay ref={overlayRef} onClick={handleOverlayClick}>
      <ModalContent>
        <Title>{confirmationModalData.title}</Title>
        <Text>{confirmationModalData.text}</Text>
        <ButtonContainer>
          <ButtonGroup
            progressButton={{
              text: confirmationModalData.confirmText || "Bestätigen",
              onClick: handleConfirm,
              alwaysActive: true,
            }}
            textButton={{
              text: confirmationModalData.cancelText || "Abbrechen",
              onClick: handleCancel,
            }}
          />
        </ButtonContainer>
      </ModalContent>
    </ModalOverlay>
  );
};

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.2);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div<{ $customStyles?: ReturnType<typeof css> }>`
  background-color: ${({ theme }) => theme.colors.background};
  padding: 30px 30px 10px 30px;
  margin: 20px;
  max-width: 600px;
  border-radius: 20px;
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 20px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);

  ${({ $customStyles }) => $customStyles}
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  width: 100%;
`;

const Title = styled(H3)`
  color: ${({ theme }) => theme.colors.dark};
`;

const Text = styled(Body)`
  color: ${({ theme }) => theme.colors.medium};
  text-align: left;
`;
